import { FunctionComponent } from "react";
import { Alert, Button, Card, Collapse, Divider, Spin, Statistic, Table, Tabs, Tag, Tooltip, Typography } from "antd";
import {
    BookOutlined,
    CalendarOutlined,
    CameraOutlined,
    CheckCircleOutlined,
    ClockCircleOutlined,
    ExclamationCircleOutlined,
    FilePdfTwoTone,
    FileSearchOutlined,
    HomeOutlined,
    MailOutlined,
    PhoneOutlined,
    ReadOutlined,
    ReloadOutlined,
    SafetyOutlined,
    TeamOutlined,
    UserOutlined,
    WarningOutlined,
} from "@ant-design/icons";
import { useAuth } from "../../hooks/AuthContext";
import { useNavigate } from "react-router-dom";
import { useMediaQuery } from "react-responsive";
import { UnahtorizedAccess } from "../public/UnahtorizedAccess";
import useMesInscriptions from "./hooks/useMesInscriptions";
import useParametres from "./hooks/useParametres";
import useBulletins from "./hooks/useBulletins";
import { EleveDto, InscriptionAdulteParAnneeScolaireDto, InscriptionEnfantParAnneeScolaireDto, ResponsableLegalDto } from "../../services/mesInscriptions";
import { StatutInscription } from "../../services/inscription";
import { buildUrlWithParams, DOCUMENT_CONTENT_ENDPOINT } from "../../services/services";
import { ROLE_UTILISATEUR } from "../../services/user";
import { getLibelleNiveauInterneAdulte, getLibelleStatutProfessionnel } from "../../components/common/commoninputs";
import { useMatieresStore } from "../../components/stores/useMatieresStore";
import { TypeMatiereEnum } from "../../services/classe";
import dayjs from "dayjs";
import { firstLettertoUpperCase } from "../../utils/FormUtils";

const getStatutConfig = (statut: StatutInscription) => {
    switch (statut) {
        case StatutInscription.VALIDEE:
            return { color: "success", icon: <CheckCircleOutlined />, text: "Validée" };
        case StatutInscription.PROVISOIRE:
            return { color: "processing", icon: <ClockCircleOutlined />, text: "Provisoire" };
        case StatutInscription.LISTE_ATTENTE:
            return { color: "warning", icon: <ExclamationCircleOutlined />, text: "Liste d'attente" };
        case StatutInscription.REFUSE:
            return { color: "error", icon: <ExclamationCircleOutlined />, text: "Refusée" };
        default:
            return { color: "default", icon: <ClockCircleOutlined />, text: statut };
    }
};

// ─── Collapse label (2 lignes : année prominent + numéro & statut) ────────────

const getCollapseLabel = (
    inscription: { anneeDebut: number; anneeFin: number; noInscription: string; statut: StatutInscription }
) => {
    const statutConfig = getStatutConfig(inscription.statut);
    return (
        <div className="dashboard-util-collapse-label">
            <div className="dashboard-util-collapse-year">
                <CalendarOutlined style={{ marginRight: 6, color: "#4FC3F7" }} />
                <span style={{ color: '#ffffff', fontWeight: 700 }}>{inscription.anneeDebut} / {inscription.anneeFin}</span>
            </div>
            <div className="dashboard-util-collapse-meta">
                <span className="dashboard-util-collapse-no" style={{ color: 'rgba(255,255,255,0.65)' }}>N° {inscription.noInscription}</span>
                <Tag
                    color={statutConfig.color}
                    icon={statutConfig.icon}
                    style={{ margin: 0, fontSize: 11 }}
                >
                    {statutConfig.text}
                </Tag>
            </div>
        </div>
    );
};

// ─── Info row helper (icône + label gris + valeur) ────────────────────────────

const InfoRow: FunctionComponent<{
    icon: React.ReactNode;
    label: string;
    value: React.ReactNode;
}> = ({ icon, label, value }) => (
    <div className="dashboard-util-info-row">
        <span className="dashboard-util-info-icon">{icon}</span>
        <span className="dashboard-util-info-label">{label}</span>
        <span className="dashboard-util-info-value">{value}</span>
    </div>
);

// ─── Responsable légal — modernisé (lignes avec icônes) ─────────────────────

const ResponsableLegalCard: FunctionComponent<{ responsableLegal: ResponsableLegalDto }> = ({ responsableLegal }) => {
    const hasContactUrgence = !!responsableLegal.nomAutre;

    return (
        <Card size="small" style={{ marginBottom: 16 }}>
            <InfoRow
                icon={<UserOutlined />}
                label="Nom & prénom"
                value={<strong>{responsableLegal.prenom} {responsableLegal.nom}</strong>}
            />
            <Divider style={{ margin: "8px 0" }} />
            <InfoRow
                icon={<MailOutlined />}
                label="Email"
                value={responsableLegal.email}
            />
            <Divider style={{ margin: "8px 0" }} />
            <InfoRow
                icon={<PhoneOutlined />}
                label="Mobile"
                value={responsableLegal.mobile}
            />
            <Divider style={{ margin: "8px 0" }} />
            <InfoRow
                icon={<HomeOutlined />}
                label="Adresse"
                value={`${responsableLegal.numeroEtRue}, ${responsableLegal.codePostal} ${responsableLegal.ville}`}
            />
            <Divider style={{ margin: "12px 0 8px" }}>
                <Tag color="blue" icon={<SafetyOutlined />}>Autorisations</Tag>
            </Divider>
            <InfoRow
                icon={<HomeOutlined />}
                label="Retour seul après l'école"
                value={responsableLegal.autorisationAutonomie
                    ? <Tag color="green">Autorisé</Tag>
                    : <Tag color="red">Non autorisé</Tag>
                }
            />
            <Divider style={{ margin: "8px 0" }} />
            <InfoRow
                icon={<CameraOutlined />}
                label="Photos / vidéos"
                value={responsableLegal.autorisationMedia
                    ? <Tag color="green">Autorisé</Tag>
                    : <Tag color="red">Non autorisé</Tag>
                }
            />

            {hasContactUrgence && (
                <>
                    <Divider style={{ margin: "12px 0 8px" }}>
                        <Tag color="orange" icon={<WarningOutlined />}>Contact d'urgence</Tag>
                    </Divider>
                    <InfoRow
                        icon={<UserOutlined />}
                        label="Nom & prénom"
                        value={<strong>{responsableLegal.prenomAutre} {responsableLegal.nomAutre}</strong>}
                    />
                    <Divider style={{ margin: "8px 0" }} />
                    <InfoRow
                        icon={<UserOutlined />}
                        label="Lien de parenté"
                        value={responsableLegal.lienParente ?? "—"}
                    />
                    <Divider style={{ margin: "8px 0" }} />
                    <InfoRow
                        icon={<PhoneOutlined />}
                        label="Téléphone"
                        value={responsableLegal.telephoneAutre ?? "—"}
                    />
                </>
            )}
        </Card>
    );
};

// ─── Section title helper ─────────────────────────────────────────────────────

const SectionTitle: FunctionComponent<{ icon: React.ReactNode; title: string }> = ({ icon, title }) => (
    <div className="dashboard-util-section-title">
        <span className="dashboard-util-section-icon">{icon}</span>
        <Typography.Text strong style={{ fontSize: 14 }}>{title}</Typography.Text>
    </div>
);

// ─── Formatage période bulletin (mois en toutes lettres + année) ──────────────

const formatPeriodeBulletin = (mois?: number, annee?: number): string => {
    const libelleMois = mois ? firstLettertoUpperCase(dayjs().month(mois - 1).format("MMMM")) : "";
    return [libelleMois, annee].filter(Boolean).join(" ").trim() || "période inconnue";
};

// ─── Bulletins d'un élève (téléchargement PDF) ────────────────────────────────

const BulletinsEleveSection: FunctionComponent<{ eleve: EleveDto; isMobile: boolean }> = ({ eleve, isMobile }) => {
    const { bulletins, isLoading } = useBulletins(eleve.id);

    if (isLoading) {
        return <Spin spinning={true} size="small" style={{ marginRight: 8 }} />;
    }

    if (!bulletins || bulletins.length === 0) {
        return (
            <div style={{ fontSize: 12, color: "#999", fontStyle: "italic" }}>
                Aucun bulletin disponible
            </div>
        );
    }

    return (
        <div>
            {bulletins.map((bulletin) => {
                return (
                    <div
                        key={bulletin.id}
                        style={{
                            display: "flex",
                            alignItems: "center",
                            gap: 12,
                            justifyContent: "space-between",
                            padding: "8px 0",
                            borderBottom: "1px solid #f0f0f0",
                            fontSize: 12,
                        }}
                    >
                        <span style={{ color: "rgba(0, 0, 0, 0.85)" }}>
                            {formatPeriodeBulletin(bulletin.mois, bulletin.annee)}
                        </span>
                        {bulletin.idDocument && (
                            <Tooltip title="Télécharger le bulletin">
                                <Button
                                    type="primary"
                                    size="small"
                                    icon={<FilePdfTwoTone />}
                                    onClick={() => {
                                        const url = buildUrlWithParams(DOCUMENT_CONTENT_ENDPOINT, { idDocument: bulletin.idDocument });
                                        window.open(`${process.env.REACT_APP_BASE_URL_API_V1}${url}`, '_blank');
                                    }}
                                >
                                    {isMobile ? "PDF" : "Télécharger"}
                                </Button>
                            </Tooltip>
                        )}
                    </div>
                );
            })}
        </div>
    );
};

// ─── Document d'inscription (téléchargement PDF) ──────────────────────────────

const DocumentInscriptionCard: FunctionComponent<{ idDocument?: number }> = ({ idDocument }) => {
    if (!idDocument) {
        return null;
    }
    return (
        <Card size="small" style={{ marginBottom: 16 }}>
            <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", flexWrap: "wrap", gap: 12 }}>
                <span style={{ fontSize: 14 }}>
                    <FilePdfTwoTone style={{ marginRight: 8 }} />
                    Votre document d'inscription
                </span>
                <Tooltip title="Télécharger votre document d'inscription">
                    <Button
                        icon={<FilePdfTwoTone />}
                        type="primary"
                        onClick={() => {
                            const url = buildUrlWithParams(DOCUMENT_CONTENT_ENDPOINT, { idDocument });
                            window.open(`${process.env.REACT_APP_BASE_URL_API_V1}${url}`, '_blank');
                        }}
                    >
                        Télécharger PDF
                    </Button>
                </Tooltip>
            </div>
        </Card>
    );
};

// ─── Élèves — mobile cards ────────────────────────────────────────────────────

const EleveCard: FunctionComponent<{ eleve: EleveDto }> = ({ eleve }) => (
    <div className="dashboard-util-eleve-card">
        <div className="dashboard-util-eleve-header">
            <div className="dashboard-util-eleve-identity">
                <span className="dashboard-util-eleve-name">{eleve.prenom} {eleve.nom}</span>
                <span className="dashboard-util-eleve-dob">{eleve.dateNaissance}</span>
            </div>
            <div className="dashboard-util-eleve-badges">
                <Tag color="blue">{eleve.niveau}</Tag>
                {eleve.niveauInterne && <Tag color="purple">{eleve.niveauInterne}</Tag>}
            </div>
        </div>
        <Divider style={{ margin: "8px 0" }} />
        <div style={{ fontSize: 12, fontWeight: 500, marginBottom: 4, color: "rgba(0,0,0,0.65)" }}>
            Bulletins
        </div>
        <BulletinsEleveSection eleve={eleve} isMobile={true} />
    </div>
);

const getColonnesEleves = () => [
    {
        title: "Nom",
        dataIndex: "nom",
        key: "nom",
    },
    {
        title: "Prénom",
        dataIndex: "prenom",
        key: "prenom",
    },
    {
        title: "Date de naissance",
        dataIndex: "dateNaissance",
        key: "dateNaissance",
    },
    {
        title: "Niveau scolaire",
        dataIndex: "niveau",
        key: "niveau",
    },
    {
        title: "Niveau interne",
        dataIndex: "niveauInterne",
        key: "niveauInterne",
        render: (value: string | null) => value ?? "—",
    },
];

// ─── Bandeau réinscription ────────────────────────────────────────────────────

const BandeauReinscription: FunctionComponent<{
    anneeDebut: number;
    anneeFin: number;
    onReinscription: () => void;
    isMobile: boolean;
}> = ({ anneeDebut, anneeFin, onReinscription, isMobile }) => (
    <Alert
        style={{ marginTop: 16 }}
        type="info"
        showIcon
        message="Réinscription prioritaire disponible"
        description={
            <span>
                En tant qu'inscrit(e) cette année, vous pouvez vous réinscrire prioritairement pour l'année scolaire{" "}
                <strong>{anneeDebut + 1} / {anneeFin + 1}</strong>.
            </span>
        }
        action={
            <Button
                type="primary"
                size={isMobile ? "middle" : "middle"}
                icon={<ReloadOutlined />}
                onClick={onReinscription}
                style={{ backgroundColor: "#52c41a", borderColor: "#52c41a", whiteSpace: "nowrap" }}
            >
                Me réinscrire
            </Button>
        }
    />
);

// ─── Récap inscription (Statistic + badge statut) ─────────────────────────────

const RecapInscription: FunctionComponent<{
    noInscription: string;
    statut: StatutInscription;
    montantTotal: number;
}> = ({ noInscription, statut, montantTotal }) => {
    const statutConfig = getStatutConfig(statut);
    return (
        <div className="dashboard-util-recap">
            <div className="dashboard-util-recap-item">
                <span className="dashboard-util-recap-label">N° inscription</span>
                <span className="dashboard-util-recap-value">{noInscription}</span>
            </div>
            <div className="dashboard-util-recap-divider" />
            <div className="dashboard-util-recap-item">
                <span className="dashboard-util-recap-label">Statut</span>
                <Tag color={statutConfig.color} icon={statutConfig.icon} style={{ fontSize: 13, padding: "4px 8px", margin: 0 }}>
                    {statutConfig.text}
                </Tag>
            </div>
            <div className="dashboard-util-recap-divider" />
            <div className="dashboard-util-recap-item">
                <Statistic
                    title={<span style={{ fontSize: 12, color: "rgba(0,0,0,0.45)" }}>Montant total</span>}
                    value={montantTotal}
                    precision={2}
                    suffix="€"
                    valueStyle={{ fontSize: 20, fontWeight: 700, color: "#0c4a6e" }}
                />
            </div>
        </div>
    );
};

// ─── Inscription enfant par année ─────────────────────────────────────────────

const InscriptionEnfantAnnee: FunctionComponent<{
    inscription: InscriptionEnfantParAnneeScolaireDto;
    shouldShowReinscriptionButton: boolean;
    onReinscription: (inscription: InscriptionEnfantParAnneeScolaireDto) => void;
}> = ({ inscription, shouldShowReinscriptionButton, onReinscription }) => {
    const isMobile = useMediaQuery({ maxWidth: 768 });

    return (
        <div>
            <RecapInscription
                noInscription={inscription.noInscription}
                statut={inscription.statut}
                montantTotal={inscription.montantTotal}
            />

            <SectionTitle icon={<UserOutlined />} title="Responsable légal" />
            <ResponsableLegalCard responsableLegal={inscription.responsableLegal} />

            <SectionTitle icon={<TeamOutlined />} title={`Élèves inscrits (${inscription.eleves.length})`} />
            {isMobile ? (
                <div style={{ marginBottom: 16 }}>
                    {inscription.eleves.map((eleve) => (
                        <EleveCard
                            key={`${eleve.nom}-${eleve.prenom}-${eleve.dateNaissance}`}
                            eleve={eleve}
                        />
                    ))}
                </div>
            ) : (
                <Table<EleveDto>
                    dataSource={inscription.eleves}
                    columns={getColonnesEleves()}
                    rowKey={(record) => `${record.nom}-${record.prenom}-${record.dateNaissance}`}
                    pagination={false}
                    size="small"
                    style={{ marginBottom: 16 }}
                    expandable={{
                        expandedRowRender: (record) => (
                            <div>
                                <div style={{ fontSize: 12, fontWeight: 500, marginBottom: 8 }}>Bulletins</div>
                                <BulletinsEleveSection eleve={record} isMobile={false} />
                            </div>
                        ),
                    }}
                />
            )}

            <SectionTitle icon={<FilePdfTwoTone />} title="Document d'inscription" />
            <DocumentInscriptionCard idDocument={inscription.idDocument} />

            {shouldShowReinscriptionButton && (
                <BandeauReinscription
                    anneeDebut={inscription.anneeDebut}
                    anneeFin={inscription.anneeFin}
                    onReinscription={() => onReinscription(inscription)}
                    isMobile={isMobile}
                />
            )}
        </div>
    );
};

// ─── Inscription adulte par année ─────────────────────────────────────────────

const InscriptionAdulteAnnee: FunctionComponent<{
    inscription: InscriptionAdulteParAnneeScolaireDto;
    shouldShowReinscriptionButton: boolean;
    onReinscription: (inscription: InscriptionAdulteParAnneeScolaireDto) => void;
}> = ({ inscription, shouldShowReinscriptionButton, onReinscription }) => {
    const { getMatieresByType } = useMatieresStore();
    const matieresAdultes = getMatieresByType(TypeMatiereEnum.ADULTE);
    const isMobile = useMediaQuery({ maxWidth: 768 });

    const getLibelleMatiere = (code: string): string => {
        const found = matieresAdultes.find((m) => m.code === code);
        return found?.fr ?? code;
    };

    return (
        <div>
            <RecapInscription
                noInscription={inscription.noInscription}
                statut={inscription.statut}
                montantTotal={inscription.montantTotal}
            />

            <SectionTitle icon={<UserOutlined />} title="Informations personnelles" />
            <Card size="small" style={{ marginBottom: 16 }}>
                <InfoRow icon={<UserOutlined />} label="Nom & prénom" value={<strong>{inscription.prenom} {inscription.nom}</strong>} />
                <Divider style={{ margin: "8px 0" }} />
                <InfoRow icon={<MailOutlined />} label="Email" value={inscription.email} />
                <Divider style={{ margin: "8px 0" }} />
                <InfoRow icon={<PhoneOutlined />} label="Mobile" value={inscription.mobile} />
                <Divider style={{ margin: "8px 0" }} />
                <InfoRow icon={<HomeOutlined />} label="Adresse" value={`${inscription.numeroEtRue}, ${inscription.codePostal} ${inscription.ville}`} />
                <Divider style={{ margin: "8px 0" }} />
                <InfoRow icon={<UserOutlined />} label="Date de naissance" value={inscription.dateNaissance} />
                <Divider style={{ margin: "8px 0" }} />
                <InfoRow icon={<UserOutlined />} label="Sexe" value={inscription.sexe === "M" ? "Homme" : "Femme"} />
                <Divider style={{ margin: "8px 0" }} />
                <InfoRow icon={<ReadOutlined />} label="Niveau" value={getLibelleNiveauInterneAdulte(inscription.niveauInterne)} />
                <Divider style={{ margin: "8px 0" }} />
                <InfoRow icon={<UserOutlined />} label="Statut professionnel" value={getLibelleStatutProfessionnel(inscription.statutProfessionnel)} />
            </Card>

            <SectionTitle icon={<BookOutlined />} title="Enseignements" />
            <div className="dashboard-util-matieres">
                {inscription.matieres.map((m) => (
                    <Tag key={m} color="blue" style={{ fontSize: 13, padding: "4px 10px" }}>
                        {getLibelleMatiere(m)}
                    </Tag>
                ))}
            </div>

            <SectionTitle icon={<FilePdfTwoTone />} title="Document d'inscription" />
            <DocumentInscriptionCard idDocument={inscription.idDocument} />

            {shouldShowReinscriptionButton && (
                <BandeauReinscription
                    anneeDebut={inscription.anneeDebut}
                    anneeFin={inscription.anneeFin}
                    onReinscription={() => onReinscription(inscription)}
                    isMobile={isMobile}
                />
            )}
        </div>
    );
};

// ─── Composant principal ──────────────────────────────────────────────────────

const MesInscriptions: FunctionComponent = () => {
    const { username, roles } = useAuth();
    const { inscriptionsEnfants, inscriptionsAdultes, isLoading } = useMesInscriptions();
    const { reinscriptionPrioritaire, isInscriptionsEnfantFermees, isInscriptionsAdulteFermees } = useParametres();
    const navigate = useNavigate();
    const isMobile = useMediaQuery({ maxWidth: 768 });

    if (!username || !roles?.includes(ROLE_UTILISATEUR)) {
        return <UnahtorizedAccess />;
    }

    const shouldShowReinscriptionButton = (inscription: InscriptionEnfantParAnneeScolaireDto) => {
        const currentYear = new Date().getFullYear();
        const isCurrentYear = inscription.anneeFin === currentYear;
        const hasNewerInscription = inscriptionsEnfants.some((i) => i.anneeFin > inscription.anneeFin);
        return isCurrentYear && reinscriptionPrioritaire && !isInscriptionsEnfantFermees && !hasNewerInscription;
    };

    const shouldShowReinscriptionAdulteButton = (inscription: InscriptionAdulteParAnneeScolaireDto) => {
        const currentYear = new Date().getFullYear();
        const isCurrentYear = inscription.anneeFin === currentYear;
        const hasNewerInscription = inscriptionsAdultes.some((i) => i.anneeFin > inscription.anneeFin);
        return isCurrentYear && !isInscriptionsAdulteFermees && !hasNewerInscription;
    };

    const handleReinscription = (inscription: InscriptionEnfantParAnneeScolaireDto) => {
        navigate("/reinscriptionEnfants", { state: { inscription } });
    };

    const handleReinscriptionAdulte = (inscription: InscriptionAdulteParAnneeScolaireDto) => {
        navigate("/reinscriptionAdultes", { state: { inscription } });
    };

    const hasEnfants = inscriptionsEnfants.length > 0;
    const hasAdultes = inscriptionsAdultes.length > 0;
    const hasNoInscription = !hasEnfants && !hasAdultes && !isLoading;

    const collapseEnfantItems = inscriptionsEnfants.map((inscription) => ({
        key: `enfant-${inscription.anneeDebut}-${inscription.anneeFin}`,
        label: getCollapseLabel(inscription),
        children: (
            <InscriptionEnfantAnnee
                inscription={inscription}
                shouldShowReinscriptionButton={shouldShowReinscriptionButton(inscription)}
                onReinscription={handleReinscription}
            />
        ),
    }));

    const collapseAdulteItems = inscriptionsAdultes.map((inscription) => ({
        key: `adulte-${inscription.anneeDebut}-${inscription.anneeFin}`,
        label: getCollapseLabel(inscription),
        children: (
            <InscriptionAdulteAnnee
                inscription={inscription}
                shouldShowReinscriptionButton={shouldShowReinscriptionAdulteButton(inscription)}
                onReinscription={handleReinscriptionAdulte}
            />
        ),
    }));

    return (
        <div className="centered-content">
            <div className="container-full-width">
                {/* ── Hero ── */}
                <div style={{
                    display: "flex",
                    alignItems: "center",
                    gap: "16px",
                    marginTop: "16px",
                    paddingBottom: "16px",
                    borderBottom: "3px solid #4FC3F7",
                    marginBottom: "24px",
                }}>
                    <div style={{
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        width: "48px",
                        height: "48px",
                        borderRadius: "10px",
                        backgroundColor: "#4FC3F7",
                        flexShrink: 0,
                    }}>
                        <BookOutlined style={{ fontSize: "24px", color: "#ffffff" }} />
                    </div>
                    <div>
                        <Typography.Title level={3} style={{ margin: 0, lineHeight: 1.2 }}>
                            Mes inscriptions
                        </Typography.Title>
                        <Typography.Text type="secondary">
                            Bienvenue, {username} — retrouvez ici l'ensemble de vos inscriptions
                        </Typography.Text>
                    </div>
                </div>

                <Spin spinning={isLoading} size="large" tip="Chargement...">

                    {/* ── Empty state ── */}
                    {hasNoInscription && (
                        <div className="dashboard-util-empty">
                            <FileSearchOutlined className="dashboard-util-empty-icon" />
                            <Typography.Title level={4} style={{ margin: "16px 0 8px", color: "rgba(0,0,0,0.65)" }}>
                                Aucune inscription
                            </Typography.Title>
                            <Typography.Text type="secondary" style={{ display: "block", marginBottom: 24, textAlign: "center", maxWidth: 400 }}>
                                Vous n'avez pas encore d'inscription. Inscrivez vos enfants ou inscrivez-vous aux cours adultes.
                            </Typography.Text>
                            <div className="dashboard-util-empty-actions">
                                <Button
                                    type="primary"
                                    size="large"
                                    icon={<TeamOutlined />}
                                    onClick={() => navigate("/inscriptionEnfants")}
                                >
                                    Inscrire mes enfants
                                </Button>
                                <Button
                                    size="large"
                                    icon={<ReadOutlined />}
                                    onClick={() => navigate("/inscriptionAdultes")}
                                >
                                    M'inscrire aux cours adultes
                                </Button>
                            </div>
                        </div>
                    )}

                    {/* ── Les deux types : onglets card ── */}
                    {hasEnfants && hasAdultes && (
                        <Tabs
                            defaultActiveKey="enfants"
                            type="card"
                            size={isMobile ? "small" : "middle"}
                            items={[
                                {
                                    key: "enfants",
                                    label: (
                                        <span>
                                            <TeamOutlined style={{ marginRight: 6 }} />
                                            Cours enfants
                                            <span className="dashboard-util-tab-badge">{inscriptionsEnfants.length}</span>
                                        </span>
                                    ),
                                    children: (
                                        <Collapse
                                            defaultActiveKey={[`enfant-${inscriptionsEnfants[0].anneeDebut}-${inscriptionsEnfants[0].anneeFin}`]}
                                            items={collapseEnfantItems}
                                        />
                                    ),
                                },
                                {
                                    key: "adultes",
                                    label: (
                                        <span>
                                            <ReadOutlined style={{ marginRight: 6 }} />
                                            Cours adultes
                                            <span className="dashboard-util-tab-badge">{inscriptionsAdultes.length}</span>
                                        </span>
                                    ),
                                    children: (
                                        <Collapse
                                            defaultActiveKey={[`adulte-${inscriptionsAdultes[0].anneeDebut}-${inscriptionsAdultes[0].anneeFin}`]}
                                            items={collapseAdulteItems}
                                        />
                                    ),
                                },
                            ]}
                        />
                    )}

                    {/* ── Enfants uniquement ── */}
                    {hasEnfants && !hasAdultes && (
                        <Collapse
                            defaultActiveKey={[`enfant-${inscriptionsEnfants[0].anneeDebut}-${inscriptionsEnfants[0].anneeFin}`]}
                            items={collapseEnfantItems}
                        />
                    )}

                    {/* ── Adultes uniquement ── */}
                    {hasAdultes && !hasEnfants && (
                        <Collapse
                            defaultActiveKey={[`adulte-${inscriptionsAdultes[0].anneeDebut}-${inscriptionsAdultes[0].anneeFin}`]}
                            items={collapseAdulteItems}
                        />
                    )}
                </Spin>
            </div>
        </div>
    );
};

export default MesInscriptions;
